import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PapiDeviation } from '@wix/da-papi-types';
import { getCurrentUserId } from '@wix/da-shared-react/pkg/publicSession/selectors';
import TierPromotionCardDumb from '@wix/da-shared-react/pkg/DeviationViews/TierPromotionCardDumb';
import TierProductCardDumb from '@wix/da-shared-react/pkg/DeviationViews/TierProductCardDumb';
import EditTierButton from '@wix/da-shared-react/pkg/Tier/EditTierButton';
import SubscribeButton from '@wix/da-shared-react/pkg/Button/SubscribeButton';
import Slider from '@wix/da-shared-react/pkg/Slider';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import ArrowDownIcon from '@wix/da-ds/pkg/Icons/16x16/ArrowDown';
import ArrowUpIcon from '@wix/da-ds/pkg/Icons/ArrowUp';
import AddTierCard from './AddTierCard';

import s from './Tiers.scss';

export interface Props {
  moduleId: number;
  tiers: PapiDeviation[];
  seeAllUrl?: string;
  isOwner: boolean;
  view?: 'promotion' | 'product';
  selectedTierId?: number;
  onClick?: (tier: PapiDeviation) => void;
  withExpandButton?: boolean;
}

const tierDeviationToPromotionCardProps = (tierDeviation: PapiDeviation) => {
  const { title = '', tier, coverImage, extended, author } = tierDeviation;
  const { description = '' } = extended || {};
  const {
    benefits = [],
    dollarPrice: formattedPrice,
    stats: { subscribers: numSubscribers = 0 } = {},
  } = tier || {};
  // Fields missing from the API
  return {
    title,
    description,
    author,
    benefits,
    coverImage,
    numSubscribers,
    formattedPrice,
  };
};

const Tiers: React.FC<Props> = ({
  tiers,
  isOwner,
  view = 'promotion',
  withExpandButton,
  selectedTierId,
  onClick,
}) => {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const count = tiers.length;
  const currentUserId = useSelector(state => getCurrentUserId(state));
  const getCTAButton = useCallback(
    deviation => {
      if (deviation?.author?.userId === currentUserId) {
        // owner
        return <EditTierButton tier={deviation} />;
      }
      return <SubscribeButton tier={deviation} />;
    },
    [currentUserId]
  );
  const scrollToIndex = tiers.findIndex(
    ({ deviationId }) => deviationId === selectedTierId
  );
  return (
    <div className={s['root']}>
      <Slider
        className={classnames(s['slider'], s[view])}
        contentClassName={classnames(
          s['slider-content'],
          count < 4 && s['centered-slider-content']
        )}
        scrollToIndex={scrollToIndex}
        scrollBy={0.5}
      >
        {isOwner && <AddTierCard count={count} />}
        {tiers.map(tier => (
          <div
            className={s['slider-card']}
            key={tier.deviationId}
            onClick={onClick && (() => onClick(tier))}
          >
            {view === 'promotion' && (
              <TierPromotionCardDumb
                className={classnames(
                  s['tier-card'],
                  tier.deviationId === selectedTierId && s['selected']
                )}
                {...tierDeviationToPromotionCardProps(tier)}
                ctaButton={getCTAButton(tier)}
                width={360}
              />
            )}
            {view === 'product' && (
              <TierProductCardDumb
                className={classnames(
                  s['tier-card'],
                  tier.deviationId === selectedTierId && s['selected']
                )}
                {...tierDeviationToPromotionCardProps(tier)}
                ctaButton={getCTAButton(tier)}
                showExpanded={withExpandButton ? !isCollapsed : undefined}
              />
            )}
          </div>
        ))}
      </Slider>
      {withExpandButton && (
        <div className={s['toggle-button-wrapper']}>
          <TextButton
            variant="main"
            size="large"
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={s['toggle-button']}
            icon={isCollapsed ? ArrowDownIcon : ArrowUpIcon}
          >
            {isCollapsed
              ? t('widgets.tiers.showAllBenefits')
              : t('widgets.tiers.showLessBenefits')}
          </TextButton>
        </div>
      )}
    </div>
  );
};
Tiers.displayName = 'Tiers';

export default Tiers;
